import type { RequestDetailViewState } from "./request-detail-view-types";
import { dateTime, historyActionLabels, approvalRoleLabel, approvalStatusLabel } from "./request-detail-presentation";
import { CheckCircle2, ClipboardCheck, FileText, Sparkles } from "lucide-react";
import { can } from "../../../../../shared/auth/access";

type Props = Pick<
  RequestDetailViewState,
  | "user"
  | "detail"
  | "evaluation"
  | "evaluationError"
  | "openDecision"
  | "refreshEvaluation"
  | "refreshingEvaluation"
  | "requestDecisionBusy"
  | "contextExpanded"
  | "setContextExpanded"
  | "activeCorrection"
  | "isCompleted"
  | "managerDecision"
  | "generalManagerDecision"
  | "finalSelectionChanged"
  | "completedDecisionDataMissing"
  | "canManageSpecialistDecision"
  | "canMakeSpecialistDecision"
  | "decisionPanelContent"
>;
export function RequestContextPanel({
  user,
  detail,
  evaluation,
  evaluationError,
  openDecision,
  refreshEvaluation,
  refreshingEvaluation,
  requestDecisionBusy,
  contextExpanded,
  setContextExpanded,
  activeCorrection,
  isCompleted,
  managerDecision,
  generalManagerDecision,
  finalSelectionChanged,
  completedDecisionDataMissing,
  canManageSpecialistDecision,
  canMakeSpecialistDecision,
  decisionPanelContent,
}: Props) {
  return <aside className="detail-side specialist-context-panel">
          {can(user, "offer.read") ? (
            <section className="detail-card decision-status">
              <div className="detail-card-title">
                <Sparkles size={17} />
                <div>
                  <strong>{decisionPanelContent.title}</strong>
                </div>
              </div>
              <p>{decisionPanelContent.description}</p>
              {evaluationError ? <p role="alert">{evaluationError}</p> : null}
              {canMakeSpecialistDecision ? (
                <button
                  type="button"
                  className="specialist-decision-action"
                  onClick={openDecision}
                >
                  <ClipboardCheck size={16} aria-hidden="true" />
                  Karar ver
                </button>
              ) : user.role === "SATIN_ALMA" &&
                canManageSpecialistDecision &&
                detail.offers.length ? (
                <button
                  type="button"
                  className="specialist-decision-action"
                  onClick={() => void refreshEvaluation()}
                  disabled={requestDecisionBusy}
                >
                  <Sparkles size={16} aria-hidden="true" />
                  {refreshingEvaluation
                    ? "Değerlendiriliyor…"
                    : evaluation
                      ? "Değerlendirmeyi yenile"
                      : "Değerlendirmeyi hazırla"}
                </button>
              ) : null}
            </section>
          ) : null}

          <section className="detail-card dossier-context-card">
            <div className="detail-card-title">
              <FileText size={17} />
              <div>
                <strong>İşlem geçmişi</strong>
                <small>{detail.history.length} kayıt</small>
              </div>
            </div>
            <div className="dossier-context-content">
              <div className="history-list compact-context-list">
                {(contextExpanded
                  ? detail.history.slice().reverse()
                  : detail.history.slice(-3).reverse()
                ).map((item) => (
                  <article key={item.id}>
                    <div>
                      <strong>
                        {historyActionLabels[item.action] ?? "Sistem işlemi"}
                      </strong>
                    </div>
                    <small>
                      {item.actor ?? "Sistem"} · {dateTime(item.occurred_at)}
                    </small>
                  </article>
                ))}
                {!detail.history.length ? (
                  <p className="detail-empty">Henüz işlem geçmişi oluşmadı.</p>
                ) : null}
              </div>
            </div>
            {detail.history.length > 3 ? (
              <button
                type="button"
                className="context-expand-action"
                onClick={() => setContextExpanded((current) => !current)}
              >
                {contextExpanded ? "Son 3 kaydı göster" : "Tümünü göster"}
              </button>
            ) : null}
          </section>

          {isCompleted ? (
            <section className="detail-card completed-decision-result">
              <div className="detail-card-title">
                <CheckCircle2 size={17} />
                <div>
                  <strong>Sonuç ve karar geçmişi</strong>
                  <small>Bağlayıcı seçim ve kronolojik denetim kaydı.</small>
                </div>
              </div>
              {finalSelectionChanged ? (
                <div className="final-selection-change">
                  <strong>
                    Nihai seçim Genel Müdür tarafından değiştirildi
                  </strong>
                  <div>
                    <span>
                      <small>Önceki öneri</small>
                      <b>
                        {managerDecision?.decided_offer_supplier ??
                          detail.recommendation?.offer_supplier ??
                          "Eski kayıtta bulunmuyor"}
                      </b>
                    </span>
                    <span>
                      <small>Nihai teklif</small>
                      <b>
                        {generalManagerDecision?.decided_offer_supplier ??
                          "Eski kayıtta bulunmuyor"}
                      </b>
                    </span>
                    <span>
                      <small>Değiştiren</small>
                      <b>
                        {generalManagerDecision?.decided_by ?? "Genel Müdür"}
                      </b>
                    </span>
                    <span>
                      <small>Karar tarihi</small>
                      <b>
                        {dateTime(generalManagerDecision?.decided_at ?? null)}
                      </b>
                    </span>
                  </div>
                  <p>
                    <strong>Gerekçe:</strong>{" "}
                    {generalManagerDecision?.decision_note ??
                      "Eski kayıtta gerekçe bulunmuyor."}
                  </p>
                </div>
              ) : null}
              {completedDecisionDataMissing ? (
                <div className="legacy-decision-note">
                  Bu eski kaydın bazı teklif bağlantıları bulunmuyor. Kayıt salt
                  okunur gösteriliyor; eksik alanlar için varsayım üretilmiyor.
                </div>
              ) : null}
              <div className="decision-audit-trail">
                <article>
                  <span>1</span>
                  <div>
                    <small>Satın Alma Uzmanı önerisi</small>
                    <strong>
                      {detail.recommendation?.offer_supplier ??
                        "Teklif bağlantısı bulunmuyor"}
                    </strong>
                    <p>
                      {detail.recommendation?.created_by ?? "Kayıtlı uzman yok"}
                      {detail.recommendation?.created_at
                        ? ` · ${dateTime(detail.recommendation.created_at)}`
                        : ""}
                    </p>
                    {detail.recommendation?.rationale ? (
                      <p>{detail.recommendation.rationale}</p>
                    ) : null}
                  </div>
                </article>
                <article>
                  <span>2</span>
                  <div>
                    <small>Satın Alma Müdürü kararı</small>
                    <strong>
                      {managerDecision?.decided_offer_supplier ??
                        "Teklif bağlantısı bulunmuyor"}
                    </strong>
                    <p>
                      {managerDecision?.decided_by ??
                        "Kayıtlı karar sahibi yok"}
                      {managerDecision?.decided_at
                        ? ` · ${dateTime(managerDecision.decided_at)}`
                        : ""}
                    </p>
                    {managerDecision?.decision_note ? (
                      <p>{managerDecision.decision_note}</p>
                    ) : null}
                  </div>
                </article>
                <article>
                  <span>3</span>
                  <div>
                    <small>Genel Müdür nihai kararı</small>
                    <strong>
                      {generalManagerDecision?.decided_offer_supplier ??
                        "Teklif bağlantısı bulunmuyor"}
                    </strong>
                    <p>
                      {generalManagerDecision?.decided_by ??
                        "Kayıtlı karar sahibi yok"}
                      {generalManagerDecision?.decided_at
                        ? ` · ${dateTime(generalManagerDecision.decided_at)}`
                        : ""}
                    </p>
                    {generalManagerDecision?.decision_note ? (
                      <p>{generalManagerDecision.decision_note}</p>
                    ) : null}
                  </div>
                </article>
              </div>
            </section>
          ) : detail.approvals.some(
              (approval) => approval.decision_note || approval.decided_by,
            ) ? (
            <section className="detail-card">
              <div className="detail-card-title">
                <CheckCircle2 size={17} />
                <div>
                  <strong>Karar geçmişi</strong>
                  <small>Verilen kararlar ve gerekçeleri.</small>
                </div>
              </div>
              <div className="approval-record-list">
                {detail.approvals
                  .filter(
                    (approval) => approval.decision_note || approval.decided_by,
                  )
                  .map((approval) => (
                    <article className="approval-record" key={approval.id}>
                      <header>
                        <div>
                          <strong>{approvalRoleLabel(approval.role)}</strong>
                          <small className="approval-record-meta">
                            <span>
                              {approval.decided_by ??
                                "Karar sahibi kaydedilmedi"}
                            </span>
                            {approval.decided_at ? (
                              <time dateTime={approval.decided_at}>
                                {dateTime(approval.decided_at)}
                              </time>
                            ) : null}
                          </small>
                        </div>
                        <span
                          className={`approval-record-status is-${approval.status.toLowerCase()}`}
                        >
                          {approvalStatusLabel(approval.status)}
                        </span>
                      </header>
                      <div className="approval-record-body">
                        {approval.decision_note &&
                        approval.id !== activeCorrection?.id ? (
                          <div className="approval-record-note">
                            <small>Karar gerekçesi</small>
                            <p>{approval.decision_note}</p>
                          </div>
                        ) : null}
                        {approval.decision_kind === "MANAGER_APPROVE" &&
                        approval.decided_offer_supplier ? (
                          <div className="revision-brief">
                            <strong>Satın Alma Müdürü önerisi</strong>
                            <span>
                              {approval.decided_offer_supplier}
                              {approval.decided_offer_no
                                ? ` · ${approval.decided_offer_no}`
                                : ""}
                            </span>
                          </div>
                        ) : null}
                        {(approval.decision_kind === "GM_APPROVE" ||
                          approval.decision_kind === "GM_FINAL_OVERRIDE") &&
                        approval.decided_offer_supplier ? (
                          <div className="revision-brief">
                            <strong>
                              {approval.decision_kind === "GM_FINAL_OVERRIDE"
                                ? "Genel Müdür nihai seçimi değiştirdi"
                                : "Genel Müdür nihai seçimi onayladı"}
                            </strong>
                            <span>
                              {approval.decided_offer_supplier}
                              {approval.decided_offer_no
                                ? ` · ${approval.decided_offer_no}`
                                : ""}
                            </span>
                          </div>
                        ) : null}
                        {approval.status === "REVISION_REQUESTED" &&
                        approval.revision_target_role ? (
                          <div className="revision-brief">
                            <strong>
                              Sorumlu:{" "}
                              {approval.revision_target_role === "PLANLAMA"
                                ? "Planlama"
                                : "Satın Alma"}
                            </strong>
                            {approval.revision_fields?.length ? (
                              <span>
                                {approval.revision_fields.join(" · ")}
                              </span>
                            ) : null}
                            {approval.revision_offer_supplier ? (
                              <span>
                                Yönetici tercihi:{" "}
                                {approval.revision_offer_supplier}
                                {approval.revision_offer_no
                                  ? ` · ${approval.revision_offer_no}`
                                  : ""}
                              </span>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    </article>
                  ))}
              </div>
            </section>
          ) : null}
        </aside>;
}
